import React, { useContext } from 'react';
import { DialogContent, DialogContentText, Typography, Button, Box } from '@mui/material';
import {
  showPackagerConfigAtom,
  packagerFormDirtyAtom,
  packagerSimulateModeAtom,
  packagerDraftChangesAtom,
} from '../../../atoms/packager-atoms';
import { useAtom } from 'jotai';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';

import { useLocation, useNavigate, useParams } from 'react-router';
import PackagerStep1Packager from './PackagerStep1Packager';
import PackagerStep2Delivery from './PackagerStep2Delivery';
import PackagerStep3Templates from './PackagerStep3Templates';
import PackagerSteps from './PackagerSteps';
import { usePackagerUpdate } from '../../hooks/packager/usePackagerUpdate';
import { usePackagerSimulate } from '../../hooks/packager/usePackagerSimulate';
import useGetPackager from '../../hooks/packager/useGetPackager';
import { useQueryClient } from '@tanstack/react-query';
import { AppContext } from '../../../App';
import { AxiosError } from 'axios';
import PackagePreviewModal from '../package/PackagePreviewModal';
import { PackagePreviewResponse } from '../../../types/packager-types';

const PackagerConfigWrapper: React.FC = () => {
  const [showPackagerConfig, setShowPackagerConfig] = useAtom(showPackagerConfigAtom);
  const [isFormDirty, setIsFormDirty] = useAtom(packagerFormDirtyAtom);
  const [simulateMode, setSimulateMode] = useAtom(packagerSimulateModeAtom);
  const [draftChanges, setDraftChanges] = useAtom(packagerDraftChangesAtom);
  const { setToast } = useContext(AppContext);

  const navigate = useNavigate();
  const client = useQueryClient();

  // Get current step from query parameter
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const currentStep = Number(searchParams.get('step')) || 1;
  const { dpid } = useParams<{ dpid: string }>();
  const { packager } = useGetPackager(dpid!);

  // Local state for preview modal
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewData, setPreviewData] = React.useState<PackagePreviewResponse | null>(null);
  const [closeSettingsOnPreviewClose, setCloseSettingsOnPreviewClose] = React.useState(false);

  // Mutation that is shared between all package steps
  const updatePackagerMutation = usePackagerUpdate({
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['packager']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Packager Updated!',
      });

      // Saved successfully; clear dirty state and advance without prompting
      setIsFormDirty(false);
      handleNext(true);
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Something went wrong. ${error.message}`,
      });
    },
  });

  // Simulate mutation for testing packager changes
  const simulatePackagerMutation = usePackagerSimulate({
    onSuccess: (data: PackagePreviewResponse) => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Packager simulation completed successfully!',
      });

      // Open preview modal with simulate response
      // Ensure closing the preview will also close the settings dialog
      setCloseSettingsOnPreviewClose(true);
      setPreviewData(data);
      setPreviewOpen(true);

      // Clear draft changes and reset simulate mode
      setDraftChanges({});
      setSimulateMode(false);
      setIsFormDirty(false);
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Simulation failed. ${error.message}`,
      });
    },
  });

  const handleCloseConfirmation = () => {
    setShowPackagerConfig(false);
    // Clear dirty state on close to prevent stale prompts on reopen
    setIsFormDirty(false);

    // Reset simulate mode and draft changes when closing
    setSimulateMode(false);
    setDraftChanges({});

    // Remove the step query parameter when closing
    const newSearchParams = new URLSearchParams(search);
    newSearchParams.delete('step');
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  const handleNext = (skipGuard: boolean = false) => {
    // Only check for unsaved changes in simulate mode
    // In update mode, changes are saved at each step, so no need to warn
    if (!skipGuard && isFormDirty && simulateMode) {
      const proceed = window.confirm('You have unsaved changes. Continue without saving?');
      if (!proceed) return;
      setIsFormDirty(false);
    }

    if (currentStep < 3) {
      const nextStep = currentStep + 1;
      const newSearchParams = new URLSearchParams(search);
      newSearchParams.set('step', nextStep.toString());
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  };

  // Centralized close guard to ensure unsaved changes are confirmed across all steps
  const handleCloseGuarded = () => {
    // In simulate mode, if we have draft changes, run simulation instead of closing
    if (simulateMode && Object.keys(draftChanges).length > 0) {
      const confirmed = window.confirm('Run simulation with current changes?');
      if (confirmed) {
        handleSimulate();
      }
      return;
    }

    // Warn on unsaved changes regardless of simulate mode
    if (isFormDirty) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }

    // Reset simulate mode and draft changes when closing
    setSimulateMode(false);
    setDraftChanges({});

    handleCloseConfirmation();
  };

  const handlePrevious = () => {
    if (isFormDirty) {
      const proceed = window.confirm('You have unsaved changes. Continue without saving?');
      if (!proceed) return;
      setIsFormDirty(false);
    }

    if (currentStep > 1) {
      const previousStep = currentStep - 1;
      const newSearchParams = new URLSearchParams(search);
      newSearchParams.set('step', previousStep.toString());
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (isFormDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to navigate away?'
      );
      if (!confirmed) {
        return;
      }
    }

    const newSearchParams = new URLSearchParams(search);
    newSearchParams.set('step', stepIndex.toString());
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  // Handle beforeunload event for page refresh/navigation
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isFormDirty) {
        e.preventDefault();
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isFormDirty]);

  // Handle simulation submission
  const handleSimulate = () => {
    if (Object.keys(draftChanges).length > 0 && dpid && packager?.id) {
      simulatePackagerMutation.mutate({
        dataPoolId: dpid,
        packagerId: packager.id,
        data: draftChanges as any,
      });
    } else {
      setToast({
        variant: 'error',
        open: true,
        message: 'No changes to simulate or packager ID not found.',
      });
    }
  };

  // Function to trigger simulation from step components
  const triggerSimulation = () => {
    if (simulateMode && Object.keys(draftChanges).length > 0) {
      handleSimulate();
    }
  };

  const renderStepComponent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PackagerStep1Packager
            updatePackagerMutation={updatePackagerMutation}
            handleCloseConfirmation={handleCloseConfirmation}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            simulateMode={simulateMode}
            draftChanges={draftChanges}
            setDraftChanges={setDraftChanges}
            triggerSimulation={triggerSimulation}
          />
        );
      case 2:
        return (
          <PackagerStep2Delivery
            updatePackagerMutation={updatePackagerMutation}
            handleCloseConfirmation={handleCloseConfirmation}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            simulateMode={simulateMode}
            draftChanges={draftChanges}
            setDraftChanges={setDraftChanges}
            triggerSimulation={triggerSimulation}
          />
        );
      case 3:
        return (
          <PackagerStep3Templates
            updatePackagerMutation={updatePackagerMutation}
            handleCloseConfirmation={handleCloseConfirmation}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            simulateMode={simulateMode}
            draftChanges={draftChanges}
            setDraftChanges={setDraftChanges}
            triggerSimulation={triggerSimulation}
            simulateLoading={simulatePackagerMutation.isPending}
          />
        );
      default:
        return (
          <PackagerStep1Packager
            updatePackagerMutation={updatePackagerMutation}
            handleCloseConfirmation={handleCloseConfirmation}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            simulateMode={simulateMode}
            draftChanges={draftChanges}
            setDraftChanges={setDraftChanges}
            triggerSimulation={triggerSimulation}
          />
        );
    }
  };

  return (
    <>
      <DialogGlynt
        open={showPackagerConfig}
        closeModal={handleCloseGuarded}
        cypressId="dialog-revert-bulk-corrections"
      >
        <DialogTitleGlynt
          title={'Packager Settings'}
          closeModal={handleCloseGuarded}
          startIcon="checkbox"
        />
        <DialogContentGlynt paddingTop="20px">
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="body2">
              {' '}
              Adjust the settings of this packager. Settings are saved at each step.{' '}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}></Box>
          </Box>

          <DialogContentText sx={{ padding: '10px 3px' }}>
            <PackagerSteps
              currentStep={currentStep}
              onStepClick={handleStepClick}
              simulateMode={simulateMode}
              numberOfChanges={Object.keys(draftChanges).length}
            />
          </DialogContentText>
        </DialogContentGlynt>
        <DialogContent>
          <hr style={{ borderTop: '0.5px solid #BDC3D2' }} />
        </DialogContent>
        {renderStepComponent()}
      </DialogGlynt>

      <PackagePreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setCloseSettingsOnPreviewClose(false);
        }}
        previewData={previewData}
        packagerId={packager?.id}
        packageId={packager?.packages?.[0] || ''}
        showDownload={false}
        closePackagerSettings={closeSettingsOnPreviewClose}
      />
    </>
  );
};

export default PackagerConfigWrapper;
